<template>
  <div>
    <Head :title="pageTitle" />

    <!-- Selector de pestañas: Notas de entrega / Facturas / Todas -->
    <div class="flex flex-wrap items-center gap-2 mb-6 bg-white p-2 rounded-xl shadow-sm border border-gray-200">
      <button
        type="button"
        @click="setTab('delivery_note')"
        :class="[
          'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all',
          form.type === 'delivery_note'
            ? 'bg-teal-700 text-white shadow'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        ]"
      >
        <i class="fa fa-file-lines text-base"></i>
        <span>Notas de entrega</span>
        <span
          v-if="counts && counts.delivery_notes !== undefined"
          :class="[
            'px-2 py-0.5 rounded-full text-xs font-semibold',
            form.type === 'delivery_note' ? 'bg-teal-900 text-teal-100' : 'bg-gray-200 text-gray-700'
          ]"
        >
          {{ counts.delivery_notes }}
        </span>
      </button>

      <button
        type="button"
        @click="setTab('invoice')"
        :class="[
          'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all',
          form.type === 'invoice'
            ? 'bg-blue-700 text-white shadow'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        ]"
      >
        <i class="fa fa-file-invoice text-base"></i>
        <span>Facturas</span>
        <span
          v-if="counts && counts.invoices !== undefined"
          :class="[
            'px-2 py-0.5 rounded-full text-xs font-semibold',
            form.type === 'invoice' ? 'bg-blue-900 text-blue-100' : 'bg-gray-200 text-gray-700'
          ]"
        >
          {{ counts.invoices }}
        </span>
      </button>

      <button
        type="button"
        @click="setTab('all')"
        :class="[
          'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all',
          form.type === 'all'
            ? 'bg-gray-800 text-white shadow'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        ]"
      >
        <i class="fa fa-layer-group text-base"></i>
        <span>Todas</span>
        <span
          v-if="counts && counts.all !== undefined"
          :class="[
            'px-2 py-0.5 rounded-full text-xs font-semibold',
            form.type === 'all' ? 'bg-gray-950 text-gray-200' : 'bg-gray-200 text-gray-700'
          ]"
        >
          {{ counts.all }}
        </span>
      </button>
    </div>

    <!-- Header y Barra de herramientas -->
    <app-toolbar
      :name="singularName"
      :namePlural="pluralName" 
      :createHref="createHref"
      :exportButtons="true"
      :hiddenCreate="user.role == 'storer' || user.role == 'financial'"
    >
      <!-- Filtros de búsqueda -->
      <template v-slot:search>
        <search-filter 
          v-model="form.search" 
          class="mr-4 w-full max-w-md" 
          @reset="reset"
        >
          <label class="block text-gray-700 mb-1">Estado:</label>
          <select 
            v-model="form.state"
            class="form-select w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          >
            <option :value="null">Todos</option>
            <option v-for="state in states" :key="state.key" :value="state.key">
              {{ state.name }}
            </option>
          </select>
        </search-filter>
      </template>

      <!-- Botones de exportación -->
      <template v-slot:exportArea>
        <div class="flex items-center gap-2">
          <a  
            v-if="user.role === 'financial' || user.role === 'admin' || user.role == 'sales'"  
            :href="`/ordersDaily`" 
            target="_blank" 
            class="p-2.5 bg-red-700 hover:bg-red-800 rounded-lg transition-colors text-white"
            title="Reporte diario"
          >
            <i class="fa fa-print"></i>
          </a>
          <Link 
            v-if="user.role === 'financial' || user.role === 'admin'" 
            target="_blank" 
            :href="`/ordersReportFinancial`" 
            class="p-2.5 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors"
            title="Reporte financiero"
          >
            <i class="fa fa-file"></i>
          </Link>
        </div>
      </template>

      <!-- Botones adicionales de creación -->
      <template v-slot:createMore>
        <!-- Si está en Notas de Entrega, opción rápida de Crear Factura -->
        <Link 
          v-if="user.role !== 'storer' && user.role !== 'financial' && form.type === 'delivery_note'" 
          class="p-2.5 px-4 bg-blue-700 hover:bg-blue-800 text-white rounded-lg transition-colors font-semibold text-sm ml-2 inline-flex items-center" 
          href="/orders/create?type=invoice"
        >
          <i class="fa fa-file-invoice mr-1.5"></i>
          <span class="hidden md:inline">Crear&nbsp;</span>Factura
        </Link>

        <!-- Si está en Facturas, opción rápida de Crear Nota de Entrega -->
        <Link 
          v-if="user.role !== 'storer' && user.role !== 'financial' && form.type === 'invoice'" 
          class="p-2.5 px-4 bg-teal-700 hover:bg-teal-800 text-white rounded-lg transition-colors font-semibold text-sm ml-2 inline-flex items-center" 
          href="/orders/create?type=delivery_note"
        >
          <i class="fa fa-file-lines mr-1.5"></i>
          <span class="hidden md:inline">Crear&nbsp;</span>Nota de Entrega
        </Link>

        <!-- Botón POS -->
        <Link 
          v-if="user.role !== 'storer' && user.role !== 'financial'" 
          class="btn-blue ml-2" 
          href="/pos"
        >
          <span>Crear</span>
          <span class="hidden md:inline">&nbsp;Venta POS</span>
        </Link>
      </template>
    </app-toolbar>

    <!-- Tabla de ventas -->
    <div class="bg-white rounded-lg shadow-sm overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left bg-gray-50 text-gray-600 text-sm font-semibold border-b">
            <th class="py-4 px-6 font-semibold">
              <span v-if="form.type === 'invoice'">N° Factura</span>
              <span v-else-if="form.type === 'delivery_note'">N° Nota de Entrega</span>
              <span v-else>N° Documento</span>
            </th>
            <th class="py-4 px-6 font-semibold">Fecha</th>
            <th class="py-4 px-6 font-semibold">Tipo</th>
            <th class="py-4 px-6 font-semibold">Estado</th>
            <th class="py-4 px-6 font-semibold">Observación</th>
            <th class="py-4 px-6 font-semibold">Cliente</th>
            <th class="py-4 px-6 font-semibold">Vendedor</th>
            <th class="py-4 px-6 font-semibold">Total</th>
            <th class="py-4 px-6 font-semibold text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="order in orders.data" 
            :key="order.id" 
            :class="{
              'hover:bg-gray-50 focus-within:bg-gray-50': true,
              'bg-red-50': order.order_state === 'cancel'
            }"
          >
            <!-- Número de Documento / Orden -->
            <td class="border-t px-6 py-4">
              <Link 
                :href="`orders/${order.id}`" 
                class="hover:text-blue-600 transition-colors"
              >
                <div v-if="order.invoice_number">
                  <span class="font-bold text-blue-700">FAC #{{ order.invoice_number }}</span>
                  <span class="block text-xs text-gray-500 font-normal">Orden #{{ order.number_order ?? order.id }}</span>
                </div>
                <div v-else>
                  <span class="font-bold text-teal-800">NE #{{ order.number_order ?? order.id }}</span>
                </div>
              </Link>
            </td>

            <!-- Fecha -->
            <td class="border-t px-6 py-4">
              <Link 
                :href="`orders/${order.id}`" 
                class="block hover:text-blue-600 transition-colors text-gray-700"
              >
                {{ order.date }}
              </Link>
            </td>

            <!-- Tipo de Documento -->
            <td class="border-t px-6 py-4">
              <span 
                v-if="order.invoice_number"
                class="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 inline-flex items-center gap-1"
              >
                <i class="fa fa-file-invoice text-xs"></i>
                Factura
              </span>
              <span 
                v-else
                class="px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 inline-flex items-center gap-1"
              >
                <i class="fa fa-file-lines text-xs"></i>
                Nota de entrega
              </span>
            </td>

            <!-- Estado -->
            <td class="border-t px-6 py-4">
              <Link 
                :href="`orders/${order.id}`" 
                class="block hover:text-blue-600 transition-colors"
              >
                <span :class="{
                  'px-2 py-1 rounded text-sm font-medium': true,
                  'bg-yellow-100 text-yellow-800': order.order_state === 'estimate',
                  'bg-red-100 text-red-800': order.order_state === 'cancel',
                  'bg-blue-100 text-blue-800': order.order_state === 'request',
                  'bg-green-100 text-green-800': order.order_state === 'finish',
                  'bg-purple-100 text-purple-800': order.order_state === 'pay',
                  'bg-orange-100 text-orange-800': order.order_state === 'delivery'
                }">
                  {{ getStateName(order.order_state) }}
                </span>
              </Link>
            </td>

            <!-- Observación -->
            <td class="border-t px-6 py-4">
              <Link 
                :href="`orders/${order.id}`" 
                class="block hover:text-blue-600 transition-colors text-gray-600 max-w-xs truncate"
              >
                {{ order.comentary || 'Sin observaciones' }}
              </Link>
            </td>

            <!-- Cliente -->
            <td class="border-t px-6 py-4">
              <Link 
                :href="`orders/${order.id}`" 
                class="block hover:text-blue-600 transition-colors font-medium text-gray-900"
              >
                {{ order.client ? order.client.name : 'Cliente General' }}
              </Link>
            </td>

            <!-- Vendedor -->
            <td class="border-t px-6 py-4">
              <Link 
                :href="`orders/${order.id}`" 
                class="block hover:text-blue-600 transition-colors text-gray-600"
              >
                {{ order.responsible ? `${order.responsible.first_name} ${order.responsible.last_name}` : 'Transacción directa' }}
              </Link>
            </td>

            <!-- Total -->
            <td class="border-t px-6 py-4">
              <Link 
                :href="`orders/${order.id}`" 
                class="block hover:text-blue-600 transition-colors font-bold text-gray-900"
              >
                ${{ Number(order.total).toFixed(2) }}
              </Link>
            </td>

            <!-- Acciones -->
            <td class="border-t px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1.5">
                <!-- Imprimir Factura -->
                <a
                  v-if="order.invoice_number"
                  :href="`/orders/${order.id}/report-invoice`"
                  target="_blank"
                  class="p-2 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  title="Imprimir Factura"
                >
                  <i class="fa fa-print"></i>
                </a>

                <!-- Imprimir Nota de Entrega -->
                <a
                  v-if="!order.invoice_number && order.order_state !== 'estimate'"
                  :href="`/orders/${order.id}/report-storer`"
                  target="_blank"
                  class="p-2 rounded bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                  title="Imprimir Nota de Entrega"
                >
                  <i class="fa fa-print"></i>
                </a>

                <!-- Convertir en Factura (para Notas de entrega) -->
                <button
                  v-if="!order.invoice_number && order.order_state !== 'estimate' && order.order_state !== 'cancel'"
                  @click="convertToInvoice(order)"
                  class="p-2 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                  title="Convertir en factura"
                >
                  <i class="fa fa-file-invoice"></i>
                </button>

                <!-- Ver Detalle -->
                <Link
                  :href="`orders/${order.id}`"
                  class="p-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  title="Ver detalles"
                >
                  <i class="fa fa-eye"></i>
                </Link>
              </div>
            </td>
          </tr>

          <tr v-if="!orders.data.length">
            <td class="px-6 py-12 text-center text-gray-500 border-t" colspan="9">
              <div class="flex flex-col items-center justify-center">
                <i class="fa fa-folder-open text-gray-300 text-4xl mb-3"></i>
                <p class="font-medium text-gray-600">
                  No hay {{ form.type === 'invoice' ? 'facturas' : (form.type === 'delivery_note' ? 'notas de entrega' : 'órdenes') }} disponibles
                </p>
                <p class="text-sm text-gray-400 mt-1">Pruebe ajustando los filtros de búsqueda o cree un nuevo documento.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination 
      v-if="orders.links" 
      :links="orders.links" 
      class="mt-6"
    />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import { getState } from '@/utils'
import Pagination from '@/Shared/Pagination.vue'
import SearchFilter from '@/Shared/SearchFilter.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import AppToolbar from '../../Shared/AppToolbar.vue'

export default {
  name: 'OrdersIndex',

  components: {
    SearchFilter,
    Head,
    Icon,
    Link,
    Pagination,
    AppToolbar,
  },

  layout: Layout,

  props: {
    orders: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      default: () => ({ search: '', state: null, type: 'delivery_note' })
    },
    counts: {
      type: Object,
      default: () => ({ delivery_notes: 0, invoices: 0, all: 0 })
    },
    user: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      form: {
        search: this.filters?.search || '',
        state: this.filters?.state || null,
        type: this.filters?.type || 'delivery_note'
      },
      states: [
        { key: 'request', name: 'Pendiente por pago' },
        { key: 'estimate', name: 'Presupuesto' },
        { key: 'pay', name: 'Pagada' },
        { key: 'payRevition', name: 'Pago en revisión' },
        { key: 'dispatch', name: 'Despachada' },
        { key: 'delivery', name: 'En espera de despacho' },
        { key: 'finish', name: 'Finalizada' },
        { key: 'cancel', name: 'Anulada' }
      ]
    }
  },

  computed: {
    pageTitle() {
      if (this.form.type === 'invoice') return 'Facturas'
      if (this.form.type === 'delivery_note') return 'Notas de entrega'
      return 'Ventas'
    },
    singularName() {
      if (this.form.type === 'invoice') return 'Factura'
      if (this.form.type === 'delivery_note') return 'Nota de entrega'
      return 'Venta'
    },
    pluralName() {
      if (this.form.type === 'invoice') return 'Facturas'
      if (this.form.type === 'delivery_note') return 'Notas de entrega'
      return 'Ventas'
    },
    createHref() {
      if (this.form.type === 'invoice') return '/orders/create?type=invoice'
      if (this.form.type === 'delivery_note') return '/orders/create?type=delivery_note'
      return '/orders/create'
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get('/orders', pickBy(this.form), { 
          preserveState: true,
          preserveScroll: true,
          replace: true
        })
      }, 300)
    }
  },

  methods: {
    setTab(type) {
      this.form.type = type
    },

    reset() {
      this.form = {
        search: '',
        state: null,
        type: this.form.type
      }
    },

    back() {
      window.history.back()
    },

    getStateName(state) {
      const found = this.states.find(s => s.key === state)
      return found ? found.name : state
    },

    convertToInvoice(order) {
      const correlative = prompt(
        'Ingrese el correlativo para la factura (dejar vacío para usar el mismo número de orden):',
        order.number_order || ''
      )
      
      if (correlative !== null) {
        this.$inertia.post(`/orders/${order.id}/invoice`, {
          invoice_number: correlative || order.number_order
        })
      }
    }
  }
}
</script>
